/*
 * Lightweight navigation icons keep the add-in shell independent from a new
 * icon package while still allowing every symbol to inherit active, muted, and
 * focus colors from the rail.
 */

export type NavIconName = "apply" | "live" | "review" | "selection" | "rules" | "account";

interface NavIconProps {
  name: NavIconName;
  className?: string;
}

function ApplyIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 3h7l4 4v14H7z" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M14 3v5h5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M9 12h6M9 16h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function LiveIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13 2 5 14h6l-1 8 8-13h-6z" fill="currentColor" />
    </svg>
  );
}

function ReviewIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 4h16v12H8l-4 4z" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="m9 10 2 2 4-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function SelectionIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="10" cy="10" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="m14 14 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function RulesIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 5h14M5 12h14M5 19h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 3v4M16 10v4M11 17v4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function AccountIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M4 21a8 8 0 0 1 16 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function NavIcon({ name, className }: NavIconProps): JSX.Element {
  switch (name) {
    case "live":
      return <LiveIcon className={className} />;
    case "review":
      return <ReviewIcon className={className} />;
    case "selection":
      return <SelectionIcon className={className} />;
    case "rules":
      return <RulesIcon className={className} />;
    case "account":
      return <AccountIcon className={className} />;
    case "apply":
    default:
      return <ApplyIcon className={className} />;
  }
}
